import { TableCard } from "@general-backoffice/core";
import { FormInput } from "@general-backoffice/core/index";
import type { DiscountReward } from "@investhinks/api-client";
import moment from "moment/moment";
import React from "react";
import { useTranslation } from "react-i18next";
import { Card, Progress } from "reactstrap";

import { TypedTable } from "../../../utils/typedLib";

interface RewardsTableCardProps {
  rewards?: DiscountReward[];
  isLoading: boolean;
  onClaim: () => void;
  numToShow?: number;
}

interface IndexedReward extends DiscountReward {
  index: number;
}

interface ColumnRow extends IndexedReward {
  reward: number;
  claimed: boolean;
  claimable: boolean;
  progress: number;
}

const RewardsTableCard: React.FC<RewardsTableCardProps> = ({ rewards = [], isLoading, onClaim, numToShow = 5 }) => {
  const { t } = useTranslation("referrer", { keyPrefix: "default.rewardsTableCard" });
  const [expanded, setExpanded] = React.useState(false);

  const columns = [
    {
      name: "#",
      formattedContent: (row: ColumnRow) => "#" + row.index,
      isTh: true,
    },
    {
      name: t("reward"),
      formattedContent: (row: ColumnRow) => {
        const reward = row.reward;
        return (
          <div className="d-flex">
            <span className="animation-zoom-3" style={{ transformOrigin: "left center" }}>
              {reward.toFixed(2)} €
            </span>
          </div>
        );
      },
      isTh: true,
    },
    {
      name: t("claimedAt"),
      formattedContent: (row: ColumnRow) => (row.claimedAt ? moment(row.claimedAt).format("LLL") : "No reclamado"),
    },
    {
      name: t("progress"),
      formattedContent: (row: ColumnRow) => {
        const { claimed, claimable, progress = 0 } = row;
        const style = { height: "2rem", width: "100%" };
        if (claimed) return <Progress children={t("progressClaimed")} color={"success"} style={style} value={100} />;
        else if (claimable)
          return <Progress children={t("progressToClaim")} color={"default"} style={style} value={100} />;
        else return <Progress children={progress.toFixed(0) + "%"} color={"primary"} style={style} value={progress} />;
      },
    },
  ];

  // Map API reward structure to expected structure
  const mappedRewards: ColumnRow[] = rewards.map((r, index) => ({
    ...r,
    index: index + 1,
    reward: r.amount,
    claimed: r.status === "claimed",
    claimable: r.status === "available",
    progress: r.status === "claimed" || r.status === "available" ? 100 : r.status === "pending" ? 50 : 0,
  }));

  const claimedRewards = mappedRewards.filter((r) => r.claimed);
  let expandedRewards = mappedRewards.slice(0, numToShow);

  const indexFirstWithoutClaim = mappedRewards.findIndex(({ progress }) => progress < 100);
  if (indexFirstWithoutClaim >= 1) {
    const finalIndex = indexFirstWithoutClaim + numToShow;
    expandedRewards = mappedRewards.slice(indexFirstWithoutClaim, finalIndex);
  }

  return (
    <Card className="overflow-hidden" key={expanded ? "table-expanded" : "table-no-expanded"}>
      <TableCard.Header
        title={t("title")}
        buttons={[
          {
            children: t("buttonClaim"),
            color: "success",
            size: "md",
            onClick: onClaim,
          },
        ]}
        subtitle={t("subtitle", { num: !isLoading ? claimedRewards.length : "--", total: 20 })}
        isLoading={isLoading}
      >
        <div className="flex-center h-100 mb--3">
          <FormInput.Switch label={t("seeAll")} checked={expanded} onChange={() => setExpanded(!expanded)} />
        </div>
      </TableCard.Header>
      <TypedTable
        columns={columns}
        rows={expanded ? mappedRewards : expandedRewards}
        isLoading={isLoading}
        noContentMessage={t("emptyRewards")}
        rowAmount={expanded ? 20 : numToShow}
        selectable={false}
        sortable={false}
      />
    </Card>
  );
};

export default RewardsTableCard;
